import { LIMITS, newId, renameBoard } from '@blueprint/domain'
import { useEffect, useState } from 'react'
import { Dialog } from '../../components/Dialog.js'
import { Button, Field, TextInput } from '../../components/controls.js'
import { getRepository, useBoardStore } from '../../state/board-store.js'
import { useMindmapStore } from '../../state/mindmap-store.js'
import { useUiStore, type BoardNameDialogKind } from '../../state/ui-store.js'
import { useWorkflowStore } from '../../state/workflow-store.js'

const LABELS: Record<BoardNameDialogKind, { title: string; submit: string; field: string; initial: string }> = {
  create: { title: 'New storymap', submit: 'Create storymap', field: 'Board name', initial: 'New storymap' },
  'create-mindmap': { title: 'New mindmap', submit: 'Create mindmap', field: 'Mindmap name', initial: 'New mindmap' },
  'create-workflow': { title: 'New workflow', submit: 'Create workflow', field: 'Workflow name', initial: 'New workflow' },
  'create-project': { title: 'New project', submit: 'Create project', field: 'Project name', initial: 'New project' },
  rename: { title: 'Rename board', submit: 'Save', field: 'Board name', initial: '' },
  'rename-project': { title: 'Rename project', submit: 'Save', field: 'Project name', initial: '' },
}

export function BoardNameDialog() {
  const kind = useUiStore((state) => state.boardNameDialog)
  const close = useUiStore((state) => state.setBoardNameDialog)
  const setView = useUiStore((state) => state.setView)
  const currentName = useBoardStore((state) => state.entities?.board.name ?? '')
  const selectedProject = useBoardStore((state) => state.projects.find((project) => project.id === state.selectedProject))
  const [name, setName] = useState('')
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    if (!kind) return
    setName(kind === 'rename' ? currentName : kind === 'rename-project' ? (selectedProject?.name ?? '') : LABELS[kind].initial)
    setError(null)
  }, [kind, currentName, selectedProject?.name])

  if (!kind) return null
  const labels = LABELS[kind]

  const submit = () => {
    const trimmed = name.trim()
    if (trimmed.length === 0) {
      setError(kind === 'create-project' || kind === 'rename-project' ? 'A project name is required.' : 'A board name is required.')
      return
    }
    const store = useBoardStore.getState()
    if (kind === 'create-project') {
      // Opens what it creates, like the other kinds: the new project is selected.
      void getRepository()
        .createProject({ id: newId(), name: trimmed })
        .then(async (project) => {
          await store.loadProjects()
          await store.selectProject(project.id)
        })
        .catch((caught: unknown) => useBoardStore.setState({ error: caught instanceof Error ? caught.message : 'The project could not be created.' }))
    } else if (kind === 'rename-project') {
      if (selectedProject) {
        void getRepository()
          .renameProject(selectedProject.id, trimmed)
          .then(() => store.loadProjects())
          .catch((caught: unknown) => useBoardStore.setState({ error: caught instanceof Error ? caught.message : 'The project could not be renamed.' }))
      }
    } else if (kind === 'create-mindmap') {
      // Opens what it creates, like createBoard.
      void useMindmapStore.getState().create(trimmed).then((id) => {
        if (id) setView('mindmap')
      })
    } else if (kind === 'create-workflow') {
      void useWorkflowStore.getState().create(trimmed).then((id) => {
        if (id) setView('workflow')
      })
    } else if (kind === 'create') {
      // createBoard opens what it creates, so follow it onto the board.
      void store.createBoard(trimmed).then((id) => {
        if (id) setView('board')
      })
    } else store.run((state) => renameBoard(state, trimmed))
    close(null)
  }

  return (
    <Dialog
      open
      title={labels.title}
      onClose={() => close(null)}
      onSubmit={submit}
      footer={
        <>
          <Button onClick={() => close(null)}>Cancel</Button>
          <Button variant="primary" type="submit">
            {labels.submit}
          </Button>
        </>
      }
    >
      <Field label={labels.field} error={error}>
        {(id) => (
          <TextInput
            id={id}
            data-autofocus
            value={name}
            maxLength={LIMITS.title}
            onChange={(event) => setName(event.target.value)}
          />
        )}
      </Field>
    </Dialog>
  )
}
